/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import React from 'react';
import Checkbox, { CheckboxTypes } from '../checkbox';
import Paginator, { PaginatorVariants } from '../paginator';
import { RowOverrides, ActionItemsCell } from '../table';
import RenFavourite from '../table/renderers/ren-favourite';
import './table-as-cards.scss';
import Loading from '../loading';
import EmptyState from '../empty-state';

const TableAsCards = React.forwardRef(({
  data, selectable, selectedIds, onSelect, favourable, favourite, onCellAction,
  actionItems, maxActions, onAction, primaryContent, secondaryContent,
  totalCount, pageSize, page, onPageClick, footerContent, showPagination, loading, emptyStateProps,
}, ref) => {
  const handleSelectClicked = (id) => {
    onSelect(id);
  };

  const handleCellAction = (rowId, colId, actionData) => {
    onCellAction(rowId, colId, actionData);
  };

  const handleRowAction = (rowId, actionId, dataItem) => {
    onAction(rowId, actionId, dataItem);
  };

  const getCardSelector = (dataItem) => (
    <Checkbox
      type={CheckboxTypes.CHECKBOX}
      checked={selectedIds.includes(dataItem.id)}
      onChange={() => handleSelectClicked(dataItem.id)}
      disabled={dataItem[RowOverrides.DISABLE_SELECTION]}
    />
  );

  const getFavourite = (dataItem) => (
    <RenFavourite
      data={dataItem}
      id={dataItem.id}
      accessor={favourite?.accessor}
      onAction={(actionData) => handleCellAction(dataItem.id, favourite?.id, actionData)}
    />
  );

  const getActionItems = (dataItem) => (
    <ActionItemsCell
      actionItems={actionItems}
      onAction={(id) => handleRowAction(dataItem.id, id, dataItem)}
      maxActions={maxActions}
      disabledActions={dataItem[RowOverrides.DISABLED_ACTIONS]}
      invisibleActions={dataItem[RowOverrides.INVISIBLE_ACTIONS]}
      disabled={dataItem[RowOverrides.DISABLE_KEBAB_MENU]}
    />
  );

  const renderTblAsCardsItems = () => (
    <div className="tbl-card-items">
      {data.length > 0 && data.map((dataItem) => (
        <div className="tbl-card-item">
          <div className="tbl-card-primary-section">
            {selectable && (
            <div className="tbl-card-selector">
              {getCardSelector(dataItem)}
            </div>
            )}
            {primaryContent && (
            <div className="tbl-card-primary-content">
              {primaryContent(dataItem)}
            </div>
            )}
            {favourable && (
            <div className="tbl-card-favorable">
              {getFavourite(dataItem)}
            </div>
            )}
            <div className="tbl-card-action-items">
              {getActionItems(dataItem)}
            </div>
          </div>
          {secondaryContent && (
          <div className="tbl-card-secondary-section">
            {secondaryContent(dataItem)}
          </div>
          )}
        </div>
      ))}
      {data.length > 0 && showPagination && (
      <div className="tbl-cards-paginator">
        <Paginator
          pageCount={Math.ceil(totalCount / pageSize)}
          page={page}
          onChange={onPageClick}
          variant={PaginatorVariants.MOBILE}
        />
      </div>
      )}
      {data.length > 0 && footerContent && footerContent() && (
      <div className="tbl-cards-footer-actions">
        {footerContent()}
      </div>
      )}
    </div>
  );

  return (
    <div className="tbl-as-cards-cont" ref={ref}>
      {loading && <Loading />}
      {data.length > 0
        ? renderTblAsCardsItems()
        : (
          <div className="tbl-card-empty-state">
            {loading ? '' : <EmptyState {...emptyStateProps} />}
          </div>
        )}
    </div>
  );
});

TableAsCards.propTypes = {
  selectable: PropTypes.bool,
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  data: PropTypes.arrayOf(PropTypes.shape),
  actionItems: PropTypes.arrayOf(PropTypes.shape),
  loading: PropTypes.bool,
  maxActions: PropTypes.number,
  page: PropTypes.number,
  totalCount: PropTypes.number,
  pageSize: PropTypes.number,
  onPageClick: PropTypes.func,
  onSelect: PropTypes.func,
  onSelectAll: PropTypes.func,
  onAction: PropTypes.func,
  onCellAction: PropTypes.func,
  favourable: PropTypes.bool,
  favourite: PropTypes.shape({
    accessor: PropTypes.string,
    id: PropTypes.string,
  }),
  primaryContent: PropTypes.node,
  secondaryContent: PropTypes.node,
  footerContent: PropTypes.node,
  showPagination: PropTypes.bool,
  emptyStateProps: PropTypes.shape({}),
};

TableAsCards.defaultProps = {
  selectable: true,
  selectedIds: [],
  data: [],
  actionItems: [],
  loading: false,
  maxActions: 2,
  page: 1,
  totalCount: 1,
  pageSize: 10,
  onPageClick: () => {},
  onSelect: () => {},
  onSelectAll: () => {},
  onAction: () => {},
  onCellAction: () => {},
  favourable: false,
  favourite: null,
  primaryContent: null,
  secondaryContent: null,
  footerContent: null,
  showPagination: true,
  emptyStateProps: {
    text: 'NO_RESULTS_FOUND',
    supportingText: 'TRY_ADJUSTING_SEARCH_FILTER',
  },
};

export default TableAsCards;
